import { Container, Loader, Text } from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'tabler-icons-react';
import UpdateOnboarding from '../../components/update-onboarding/update-onboarding.component';
import { UpdateOnboardFormInitialValues } from '../../data/onboard-form-initival-values.object';
import { BusinessBankAccountDepositTypes } from '../../enums/bank.enums';
import { BusinessTypes } from '../../enums/business-type';
import { MerchantOnboardingStatus } from '../../enums/merchant-onboarding-status';
import { removeEmptyPropertiesWithNested } from '../../helpers/filters.helpers';
import {
  apiToIncorporators,
  apiToSignatories,
  createUpdateMerchantOnboardingDataRequest,
  incorporatorsToApi,
  setFormData,
  signatoriesToApi,
  validateUpdateFiles,
} from '../../helpers/merchant-onboarding';
import { filesToSubmit } from '../../helpers/upload-files';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  UpdateOnboardingNonStarterSchema,
  UpdateOnboardingStarterSchema,
} from '../../schemas/update-merchant.schema';
import {
  MerchantFileMetadata,
  RetrieveMerchantResponse,
  UpdateMerchantOnboardingDataRequest,
} from '../../type/admin.types';
import { AuthType } from '../../type/auth.types';
import { ErrorResponse } from '../../type/error-response.types';
import { FormSignatory } from '../../type/incorporator-signatory.types';
import { UpdateMerchantOnboardingFormValues } from '../../type/update-merchant-onboarding-form-values.types';
import { MdrFee } from '../../type/onboard-merchant-request.types';

function MerchantUpdateOnboardingPage() {
  const [businessType, setBusinessType] = useState(BusinessTypes.STARTER);
  const [loading, setLoading] = useState(false);
  const [isRetrievedMerchant, setIsRetrievedMerchant] = useState(false);
  const [initialBusinessHandle, setInitialBusinessHandle] = useState('');
  const [declinedReason, setDeclinedReason] = useState('');
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const endpoint = `web/onboard/${auth?.id}`;
  const [merchant, setMerchant] = useState<RetrieveMerchantResponse>();
  const [isFetched, setIsFetched] = useState(false);
  const [mdrFees, setMdrFees] = useState<MdrFee[]>();

  const form = useForm<UpdateMerchantOnboardingFormValues>({
    initialValues: UpdateOnboardFormInitialValues,
    schema:
      businessType === BusinessTypes.STARTER
        ? joiResolver(UpdateOnboardingStarterSchema)
        : joiResolver(UpdateOnboardingNonStarterSchema),
  });

  //same as delete empty in admin update
  //but has delete logic for bank
  //which admin does not have
  const deleteEmpty = (
    form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
    request: UpdateMerchantOnboardingDataRequest
  ) => {
    if (
      !form.values.businessWebsiteUrl ||
      form.values.businessWebsiteUrl.trim().length < 1
    ) {
      delete request?.websites?.businessWebsiteUrl;
    }
    if (!form.values.facebookUrl || form.values.facebookUrl.trim().length < 1) {
      delete request?.websites?.facebookUrl;
    }
    if (!form.values.twitterUrl || form.values.twitterUrl.trim().length < 1) {
      delete request?.websites?.twitterUrl;
    }
    if (
      !form.values.instagramUrl ||
      form.values.instagramUrl.trim().length < 1
    ) {
      delete request?.websites?.instagramUrl;
    }
    if (!form.values.tiktokUrl || form.values.tiktokUrl.trim().length < 1) {
      delete request?.websites?.tiktokUrl;
    }
    if (form.values.businessTypeId === BusinessTypes.STARTER) {
      delete request.incorporators;
      delete request.address.registered;
      delete request.legalName;
    }

    if (!form.values.mainOfficeBldgNameNo) {
      delete request.address.office.buildingNameAndNumber;
    }

    if (!form.values.registeredBldgNameNo) {
      if (request.address.registered !== undefined) {
        delete request.address.registered.buildingNameAndNumber;
      }
    }

    request.incorporators?.forEach((incorporator) => {
      if (!incorporator.address.buildingNameAndNumber) {
        delete incorporator.address.buildingNameAndNumber;
      }
    });

    form.values.settlements.forEach((settlement) => {
      if (
        settlement.accountDepositTypeId !== BusinessBankAccountDepositTypes.BANK
      ) {
        delete settlement.accountTypeId;
      }
    });

    if (!form.values.currency) {
      request.currencyId = null;
    }
  };

  useEffect(() => {
    const fetchOnboardingData = async () => {
      setLoading(true);
      try {
        const result = await axiosPrivate.get(endpoint);
        const resData: RetrieveMerchantResponse = result.data;
        setBusinessType(resData.businessTypeId);
        const restructuredIncorporators = apiToIncorporators(
          resData.incorporators
        );

        let restructuredSignatories: FormSignatory[] = [];

        if (resData.signatories) {
          restructuredSignatories = apiToSignatories(resData.signatories);
        }

        setMerchant(resData);

        setFormData(
          form,
          resData,
          restructuredIncorporators,
          restructuredSignatories
        );
        setInitialBusinessHandle(resData.businessHandle);
        setIsRetrievedMerchant(true);
        setDeclinedReason(resData.declinedReason ?? '');
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        setIsRetrievedMerchant(false);
        showNotification({
          id: 'failed-to-retrieve-merchant',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setLoading(false);
        setIsFetched(true);
      }
    };
    fetchOnboardingData();
  }, []);

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);

    const validFiles = validateUpdateFiles(form);
    if (!validFiles) {
      setLoading(false);
      return;
    }

    const restructuredIncorporators = incorporatorsToApi(form);
    const restructuredSignatories = signatoriesToApi(form);

    try {
      const filesResult: MerchantFileMetadata[] = await filesToSubmit(
        form,
        merchant,
        values,
        auth?.id as number
      );

      const request: UpdateMerchantOnboardingDataRequest =
        createUpdateMerchantOnboardingDataRequest(
          restructuredIncorporators,
          restructuredSignatories,
          values
        );

      request.mdrFees = mdrFees;
      deleteEmpty(form, request);
      removeEmptyPropertiesWithNested(request);

      request.files = filesResult;

      await axiosPrivate.put(endpoint, request);
      showNotification({
        id: 'successfully-updated-onboarding',
        message: 'Successfully updated application',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      const newAuth: AuthType = {
        ...auth,
        onboardingStatus: MerchantOnboardingStatus.PENDING,
      };
      setAuth(newAuth);
      navigate('/onboarding/pending');
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;
      showNotification({
        id: 'failed-to-update-onboarding',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!isFetched ? (
        <Loader />
      ) : (
        <>
          {!isRetrievedMerchant ? (
            <Text>Merchant not found</Text>
          ) : (
            <Container
              fluid
              px={0}
              style={{
                margin: 0,
              }}
            >
              <Container
                fluid
                sx={{
                  '@media (min-width: 350px)': {
                    padding: 0,
                  },
                  '@media (min-width: 650px)': {
                    paddingTop: 10,
                    paddingLeft: 25,
                    paddingRight: 25,
                    paddingBottom: 25,
                  },
                  '@media (min-width: 755px)': {
                    paddingTop: 10,
                    paddingLeft: 50,
                    paddingRight: 50,
                    paddingBottom: 50,
                  },
                }}
              >
                <UpdateOnboarding
                  form={form}
                  handleSubmit={handleSubmit}
                  initialBusinessHandle={initialBusinessHandle}
                  isNonAdminInterface={true}
                  declinedReason={declinedReason}
                  loading={loading}
                  mdrFees={mdrFees}
                  setMdrFees={setMdrFees}
                />
              </Container>
            </Container>
          )}
        </>
      )}
    </>
  );
}

export default MerchantUpdateOnboardingPage;
